import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import type { AnyValue } from "@/lib/rowTypes";

export interface Friendship {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: string;
  created_at: string;
  friend_profile?: { id: string; display_name: string | null; avatar_url: string | null };
  is_household?: boolean;
}

export interface FriendChallenge {
  id: string;
  challenger_id: string;
  challenged_id: string;
  challenge_type: string;
  status: string;
  duration_hours: number;
  lesson_id: string | null;
  challenger_score: number;
  challenged_score: number;
  winner_id: string | null;
  result_state: string | null;
  finalized_at: string | null;
  started_at: string | null;
  expires_at: string | null;
  created_at: string;
  challenger_completed_at?: string | null;
  challenged_completed_at?: string | null;
  challenger_name?: string;
  challenged_name?: string;
}

export interface FriendStats {
  total_xp: number;
  level: number;
  title: string;
  login_streak: number;
  learning_streak: number;
  longest_streak: number;
  lessons_completed: number;
  achievements: number;
}

export function useFriends() {
  const { user } = useAuth();
  const [friends, setFriends] = useState<Friendship[]>([]);
  const [pendingReceived, setPendingReceived] = useState<Friendship[]>([]);
  const [pendingSent, setPendingSent] = useState<Friendship[]>([]);
  const [challenges, setChallenges] = useState<FriendChallenge[]>([]);
  const [householdMemberIds, setHouseholdMemberIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const fetchFriends = useCallback(async () => {
    if (!user) { setLoading(false); return; }

    // Household co-members for sibling tagging
    const { data: hm } = await supabase
      .from("household_members")
      .select("user_id, household_id")
      .eq("user_id", user.id);
    const householdIds = ((hm) || []).map(r => r.household_id);
    const houseSet = new Set<string>();
    if (householdIds.length > 0) {
      const { data: peers } = await supabase
        .from("household_members")
        .select("user_id, household_id")
        .in("household_id", householdIds);
      ((peers) || []).forEach(p => { if (p.user_id !== user.id) houseSet.add(p.user_id); });
    }
    setHouseholdMemberIds(houseSet);

    const { data: friendships } = await supabase
      .from("friendships")
      .select("*")
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);

    const items = (friendships) || [];
    const friendIds = new Set<string>();
    items.forEach(f => friendIds.add(f.requester_id === user.id ? f.addressee_id : f.requester_id));

    const profileMap: Record<string, AnyValue> = {};
    if (friendIds.size > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url")
        .in("id", Array.from(friendIds));
      (profiles || []).forEach(p => { profileMap[p.id] = p; });
    }

    const accepted: Friendship[] = [];
    const recvd: Friendship[] = [];
    const sent: Friendship[] = [];

    items.forEach(f => {
      const friendId = f.requester_id === user.id ? f.addressee_id : f.requester_id;
      const enriched: Friendship = {
        ...f,
        friend_profile: profileMap[friendId] || { id: friendId, display_name: null, avatar_url: null },
        is_household: houseSet.has(friendId),
      };
      if (f.status === "accepted") accepted.push(enriched);
      else if (f.status === "pending" && f.addressee_id === user.id) recvd.push(enriched);
      else if (f.status === "pending" && f.requester_id === user.id) sent.push(enriched);
    });

    setFriends(accepted);
    setPendingReceived(recvd);
    setPendingSent(sent);
    setLoading(false);
  }, [user]);

  const fetchChallenges = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("friend_challenges")
      .select("*")
      .or(`challenger_id.eq.${user.id},challenged_id.eq.${user.id}`)
      .in("status", ["pending", "active", "completed", "expired"])
      .order("created_at", { ascending: false })
      .limit(20);

    const items = (data) || [];
    const userIds = new Set<string>();
    items.forEach(c => { userIds.add(c.challenger_id); userIds.add(c.challenged_id); });
    const nameMap: Record<string, string> = {};
    if (userIds.size > 0) {
      const { data: profiles } = await supabase.from("profiles").select("id, display_name").in("id", Array.from(userIds));
      (profiles || []).forEach(p => { nameMap[p.id] = p.display_name || p.id.slice(0, 8); });
    }
    setChallenges(items.map(c => ({
      ...c,
      challenger_name: nameMap[c.challenger_id] || "Unknown",
      challenged_name: nameMap[c.challenged_id] || "Unknown",
    })));
  }, [user]);

  useEffect(() => {
    fetchFriends();
    fetchChallenges();
  }, [fetchFriends, fetchChallenges]);

  const searchUsers = async (query: string) => {
    if (!user || query.length < 2) return [];
    const { data, error } = await supabase.rpc("search_users_for_friending", { p_query: query });
    if (error) {
      toast.error(error.message);
      return [];
    }
    return (data) || [];
  };

  const sendRequest = async (friendId: string) => {
    if (!user) return;
    const { error } = await supabase.from("friendships").insert({ requester_id: user.id, addressee_id: friendId });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Friend request sent");
    await fetchFriends();
  };

  const acceptRequest = async (friendshipId: string) => {
    const { error } = await supabase.rpc("accept_friend_request", { p_friendship_id: friendshipId });
    if (error) { toast.error(error.message); return; }
    toast.success("Friend request accepted");
    await fetchFriends();
  };

  const declineRequest = async (friendshipId: string) => {
    if (!user) return;
    await supabase.from("friendships").update({ status: "declined" }).eq("id", friendshipId);
    await fetchFriends();
  };

  const removeFriend = async (friendshipId: string) => {
    await supabase.from("friendships").delete().eq("id", friendshipId);
    await fetchFriends();
  };

  const getFriendStats = async (friendId: string): Promise<FriendStats | null> => {
    const { data, error } = await supabase.rpc("get_friend_stats", { p_friend_id: friendId });
    if (error) return null;
    return data as AnyValue as FriendStats;
  };

  const sendChallenge = async (
    friendId: string,
    type: "xp_race" | "quiz_duel",
    durationHours = 24,
    lessonId?: string,
  ) => {
    if (!user) return null;
    const { data, error } = await supabase.rpc("create_challenge", {
      p_target_user_id: friendId,
      p_challenge_type: type,
      p_lesson_id: lessonId || null,
      p_duration_hours: durationHours,
    });
    if (error) { toast.error(error.message); return null; }
    toast.success("Challenge sent");
    await fetchChallenges();
    return data as string;
  };

  const sendTopicChallenge = async (friendId: string, courseName: string, count = 8, durationHours = 48) => {
    const { data, error } = await supabase.rpc("create_topic_challenge", {
      p_target_user_id: friendId, p_course_name: courseName, p_question_count: count, p_duration_hours: durationHours, p_mode: "accuracy",
    });
    if (error) { toast.error(error.message); return null; }
    toast.success(`Topic battle sent: ${courseName}`);
    await fetchChallenges();
    return data as string;
  };

  const sendScenarioChallenge = async (friendId: string, topic: string | null, count = 5, durationHours = 48) => {
    const { data, error } = await supabase.rpc("create_scenario_challenge", {
      p_target_user_id: friendId, p_topic: topic, p_count: count, p_duration_hours: durationHours,
    });
    if (error) { toast.error(error.message); return null; }
    const result = (data ?? {}) as { challenge_id?: string; fell_back_to_mixed?: boolean; requested_topic?: string };
    if (result.fell_back_to_mixed) {
      toast.success(`That topic is still growing — built your challenge with a broader set of scenarios.`);
    } else {
      toast.success("Scenario showdown sent");
    }
    await fetchChallenges();
    return (result.challenge_id ?? null) as string | null;
  };

  const sendDailyDuel = async (friendId: string) => {
    const { data, error } = await supabase.rpc("create_daily_duel", { p_target_user_id: friendId });
    if (error) { toast.error(error.message); return null; }
    toast.success("Daily duel sent");
    await fetchChallenges();
    return data as string;
  };

  const sendStreakWar = async (friendId: string, metricKey: string, durationHours = 168) => {
    const { data, error } = await supabase.rpc("create_streak_war", {
      p_target_user_id: friendId, p_metric_key: metricKey, p_duration_hours: durationHours,
    });
    if (error) { toast.error(error.message); return null; }
    toast.success("Streak war started");
    await fetchChallenges();
    return data as string;
  };

  const sendMilestoneChallenge = async (friendId: string, metricKey: string, target: number, durationHours = 168) => {
    const { data, error } = await supabase.rpc("create_milestone_challenge", {
      p_target_user_id: friendId, p_metric_key: metricKey, p_metric_target: target, p_duration_hours: durationHours,
    });
    if (error) { toast.error(error.message); return null; }
    toast.success("Milestone challenge sent");
    await fetchChallenges();
    return data as string;
  };

  const createSeries = async (friendId: string, format: 3 | 5, challengeType: "topic_battle" | "scenario_showdown", courseName?: string, topic?: string) => {
    const { data, error } = await supabase.rpc("start_series_with_first_round", {
      p_opponent_id: friendId,
      p_format: format,
      p_challenge_type: challengeType,
      p_course_name: courseName ?? null,
      p_topic: topic ?? null,
    });
    if (error) { toast.error(error.message); return null; }
    toast.success(`Best-of-${format} series started`);
    await fetchChallenges();
    return (data as AnyValue)?.challenge_id as string;
  };

  const rematchChallenge = async (originalId: string) => {
    const { data, error } = await supabase.rpc("rematch_challenge", { p_original_challenge_id: originalId });
    if (error) { toast.error(error.message); return null; }
    toast.success("Rematch sent");
    await fetchChallenges();
    return data as string;
  };

  const getRivalries = async () => {
    const { data, error } = await supabase.rpc("get_user_rivalries");
    if (error) return [];
    return (data) || [];
  };

  const acceptChallenge = async (challengeId: string) => {
    const { error } = await supabase.rpc("accept_challenge", { p_challenge_id: challengeId });
    if (error) { toast.error(error.message); return; }
    toast.success("Challenge accepted");
    await fetchChallenges();
  };

  const declineChallenge = async (challengeId: string) => {
    await supabase.from("friend_challenges").update({ status: "declined" }).eq("id", challengeId);
    await fetchChallenges();
  };

  const checkChallengeResults = async (challengeId: string) => {
    const { data } = await supabase.rpc("finalize_challenge", { p_challenge_id: challengeId });
    await fetchChallenges();
    return data;
  };

  return {
    friends,
    pendingReceived,
    pendingSent,
    challenges,
    householdMemberIds,
    loading,
    searchUsers,
    sendRequest,
    acceptRequest,
    declineRequest,
    removeFriend,
    getFriendStats,
    sendChallenge,
    sendTopicChallenge,
    sendScenarioChallenge,
    sendDailyDuel,
    sendStreakWar,
    sendMilestoneChallenge,
    createSeries,
    rematchChallenge,
    getRivalries,
    acceptChallenge,
    declineChallenge,
    checkChallengeResults,
    refetch: () => { fetchFriends(); fetchChallenges(); },
  };
}
